import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META, imageSrc } from "../lib/api";
import { FileEdit, Loader2, Trash2, Layers, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const SCOPES = [["all", "All"], ["mine", "Made by you"], ["feeds", "From feeds"], ["autopilot", "Autopilot"],
  ["failed", "Image failed"]];

export const DraftsView = ({ onResume }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");
  const [counts, setCounts] = useState({});
  const [cleaning, setCleaning] = useState(false);

  const load = async (which = scope) => {
    try {
      const [{ data }, c] = await Promise.all([api.get(`/drafts?scope=${which}`), api.get("/drafts/counts")]);
      setDrafts(data);
      setCounts(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load drafts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(scope);
    // Image jobs run on the server, so a draft opened here can still be
    // rendering. Refresh while any of them is.
    const timer = setInterval(() => load(scope), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Leftovers: earlier attempts at a feed article that were replaced, and -
  // when asked - drafts whose image never rendered.
  const cleanup = async (failed) => {
    setCleaning(true);
    try {
      const { data } = await api.post("/drafts/cleanup", { orphans: true, failed });
      toast.success(data.removed ? `Removed ${data.removed} leftover draft${data.removed === 1 ? "" : "s"}` : "Nothing to clean up");
      await load(scope);
    } catch { toast.error("Clean-up failed"); } finally { setCleaning(false); }
  };

  const remove = async (id) => {
    await api.delete(`/posts/${id}`);
    toast("Draft deleted");
    load();
  };

  const working = drafts.some((d) => ["generating", "pending"].includes(d.image_status));

  return (
    <div className="space-y-8" data-testid="drafts-view">
      <div>
        <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Work in progress</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50">Drafts</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-xl">
          Anything you started but have not submitted. Image generation continues on the
          server, so you can leave a carousel rendering and come back to it.
          {working && " Something is rendering now."}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {SCOPES.map(([k, l]) => (
          <button key={k} onClick={() => { setScope(k); setLoading(true); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${scope === k
              ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-100" : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>
            {l} <span className="opacity-60">{counts[k] ?? ""}</span>
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={() => cleanup(false)} disabled={cleaning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-semibold text-slate-300 hover:border-slate-600 disabled:opacity-50">
            {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Clear leftovers
          </button>
          {counts.failed > 0 && (
            <button onClick={() => cleanup(true)} disabled={cleaning}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-900/50 text-xs font-semibold text-rose-300 hover:bg-rose-500/10 disabled:opacity-50">
              <AlertTriangle className="w-3.5 h-3.5" /> Delete {counts.failed} with failed images
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : drafts.length === 0 ? (
        <div className="glass rounded-2xl py-20 flex flex-col items-center gap-3">
          <FileEdit className="w-8 h-8 text-slate-700" />
          <p className="text-slate-500 text-sm">No drafts yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {drafts.map((d, i) => {
            const p = d.image_progress;
            const rendering = ["generating", "pending"].includes(d.image_status);
            const done = p ? p.done + p.failed : 0;
            return (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="glass rounded-2xl overflow-hidden flex"
                data-testid={`draft-${d.id}`}
              >
                <div className="w-24 shrink-0 bg-slate-900 flex items-center justify-center">
                  {imageSrc(d) ? (
                    <img src={imageSrc(d)} alt="" className="w-full h-full object-cover" />
                  ) : rendering ? (
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-300" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-slate-700" />
                  )}
                </div>

                <div className="flex-1 min-w-0 p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-slate-100 text-sm truncate">{d.topic || "Untitled"}</p>
                    <span className="text-[11px] text-slate-500 shrink-0">{ago(d.updated_at || d.created_at)}</span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {d.fmt === "carousel" && (
                      <span className="flex items-center gap-1 text-[11px] text-slate-400">
                        <Layers className="w-3 h-3" /> {(d.slides || []).length} slides
                      </span>
                    )}
                    {(d.platforms || []).map((pl) => (
                      <span key={pl} className="text-[11px] font-semibold" style={{ color: PLATFORM_META[pl]?.color }}>
                        {PLATFORM_META[pl]?.label || pl}
                      </span>
                    ))}
                  </div>

                  {rendering && p?.total ? (
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                        <span>Rendering slide {Math.min((p.current ?? 0) + 1, p.total)} of {p.total}</span>
                        <span>{done}/{p.total}</span>
                      </div>
                      <div className="h-1 rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full bg-indigo-500 transition-all duration-500"
                          style={{ width: `${Math.round((done / p.total) * 100)}%` }} />
                      </div>
                    </div>
                  ) : d.image_status === "partial" || p?.failed ? (
                    <p className="flex items-center gap-1.5 text-[11px] text-amber-400">
                      <AlertTriangle className="w-3 h-3" />
                      {p?.failed} of {p?.total} images failed — reopen to retry those slides
                    </p>
                  ) : null}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      data-testid={`resume-${d.id}`}
                      onClick={() => onResume(d.id)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 text-xs font-semibold transition-colors"
                    >
                      Continue editing
                    </button>
                    <button
                      onClick={() => remove(d.id)}
                      className="p-1.5 rounded-lg text-slate-600 hover:text-rose-400 transition-colors"
                      aria-label="Delete draft"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
